import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../../shared/lib/api'
import { useAuthStore } from '../../auth/store'
import { Card, CardContent } from '../../../shared/components/Card'

interface InvitationInfo {
  id: string
  email: string
  role: 'admin' | 'cuidador'
  groupNombre: string
  invitedByNombre: string
  expiresAt: string
}

export function InvitationPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const { checkSession } = useAuthStore()

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nombre, setNombre] = useState('')

  useEffect(() => {
    if (!token) {
      setError('Token de invitación no válido')
      setIsLoading(false)
      return
    }

    fetchInvitation()
  }, [token])

  const fetchInvitation = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await api.get<{ invitation: InvitationInfo }>(`/api/invitations/${token}`)
      setInvitation(response.invitation)
      // Pre-fill name from email
      setNombre(response.invitation.email.split('@')[0] ?? '')
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Error al cargar la invitación'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return

    try {
      setIsAccepting(true)
      setError(null)
      await api.post(`/api/invitations/${token}/accept`, { nombre })
      // Refresh session to get the new user info
      await checkSession()
      navigate('/')
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Error al aceptar la invitación'
      setError(message)
      setIsAccepting(false)
    }
  }

  const handleReject = async () => {
    if (!token || !confirm('¿Seguro que quieres rechazar esta invitación?')) return

    try {
      setIsLoading(true)
      await api.post(`/api/invitations/${token}/reject`)
      navigate('/auth/login')
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Error al rechazar la invitación'
      setError(message)
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="loading-container">
            <div className="spinner" />
            <p>Cargando invitación...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <Card>
            <CardContent>
              <h1 className="auth-title">Invitación inválida</h1>
              <p className="text-error">{error || 'La invitación no es válida o ha expirado.'}</p>
              <button onClick={() => navigate('/auth/login')} className="btn btn-primary">
                Ir al inicio
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Card>
          <CardContent>
            <h1 className="auth-title">Invitación a {invitation.groupNombre}</h1>
            <p className="auth-subtitle">
              <strong>{invitation.invitedByNombre}</strong> te ha invitado a unirte como{' '}
              <strong>{invitation.role}</strong>
            </p>

            <form onSubmit={handleAccept} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={invitation.email}
                  disabled
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="nombre">Tu nombre</label>
                <input
                  type="text"
                  id="nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                  className="form-control"
                  placeholder="Ingresa tu nombre"
                />
              </div>

              <div className="auth-actions">
                <button
                  type="button"
                  onClick={handleReject}
                  className="btn btn-secondary"
                  disabled={isAccepting}
                >
                  Rechazar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isAccepting}>
                  {isAccepting ? 'Aceptando...' : 'Aceptar invitación'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
